import { useCallback, useEffect, useState } from "react";
import {
  getLocal,
  MDFeFiltrarDto,
  MDFeInput,
  MDFeOutput,
  STORAGEMDFE_KEY,
  updateLocal,
} from "./MDFe.Dto";
import useFilterManager from "src/hook/useFilterManager";
import { MapearErrosServidor } from "src/hook/Functions";
import {
  MDFeBuscarXML,
  MDFeConsultarSituacao,
  MDFeEnviar,
  MDFeExcluir,
  MDFeFiltrar,
  MDFeImprimirDAMDFE,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import MDFeMenuCode from "./MDFe.Menu.Code";
import moment from "moment";

const getDataInicialPadrao = () => moment().startOf("month").toDate();
const getDataFinalPadrao = () => moment().toDate();

const parseFilterDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const m = moment(dateString);
  return m.isValid() ? m.toDate() : null;
};

const areDatesSameDay = (d1?: Date | null, d2?: Date | null) => {
  if (!d1 && !d2) return true;
  if (!d1 || !d2) return false;
  return moment(d1).isSame(d2, "day");
};

const MDFeCode = () => {
  const [data, setData] = useState<MDFeOutput[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalShowPropriedades, setModalShowPropriedades] = useState(false);
  const [modalNovoShow, setModalNovoShow] = useState(false);
  const [modalNaoEncerradosShow, setModalNaoEncerradosShow] = useState(false);
  const [mdfe, setMdfe] = useState<MDFeOutput>();
  const navigate = useNavigate();

  const handleNovoMDFe = useCallback(() => {
    const mdfeSalvo = getLocal<MDFeInput>(STORAGEMDFE_KEY);
    if (mdfeSalvo && Object.keys(mdfeSalvo).length > 0) {
      setModalNovoShow(true);
    } else {
      navigate("/fiscal/mdfe/cadastro");
    }
  }, [navigate]);

  const handleConsultarNaoEncerrados = useCallback(() => {
    setModalNaoEncerradosShow(true);
  }, []);

  const menu = MDFeMenuCode({
    setLoading,
    data,
    onNovoMDFe: handleNovoMDFe,
    onConsultarNaoEncerrados: handleConsultarNaoEncerrados,
  });

  const useFilter = useFilterManager<MDFeFiltrarDto>({
    codigoStatusEnvio: null,
    dataEmissaoInicial: getDataInicialPadrao(),
    dataEmissaoFinal: getDataFinalPadrao(),
    serie: null,
    placa: null,
  });

  const filtrar = useCallback(async (input: MDFeFiltrarDto) => {
    try {
      const response = await MDFeFiltrar(input);
      if (response.data.success) {
        setData(response.data.data || []);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao filtrar MDF-e:", error);
      showToast({
        message: "Ocorreu erro ao buscar dados",
        title: "Erro",
        variant: "Danger",
      });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      try {
        await filtrar(useFilter.filters);
      } finally {
        setLoading(false);
      }
    };

    const hasValidDateRange =
      (useFilter.filters.dataEmissaoInicial &&
        useFilter.filters.dataEmissaoFinal) ||
      (!useFilter.filters.dataEmissaoInicial &&
        !useFilter.filters.dataEmissaoFinal);

    if (hasValidDateRange) {
      loadData();
    }

    return () => controller.abort();
  }, [useFilter.filters, filtrar]);

  useEffect(() => {
    const serieParam = useFilter.searchParams.get("serie");
    const placaParam = useFilter.searchParams.get("placa");
    const dataInicialParam = useFilter.searchParams.get("dataEmissaoInicial");
    const dataFinalParam = useFilter.searchParams.get("dataEmissaoFinal");
    const semDataParam = useFilter.searchParams.get("semData");

    const isInitialEmpty =
      !dataInicialParam && !dataFinalParam && semDataParam !== "true";

    const dataEmissaoInicial =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataInicialPadrao()
        : parseFilterDate(dataInicialParam);

    const dataEmissaoFinal =
      semDataParam === "true"
        ? null
        : isInitialEmpty
        ? getDataFinalPadrao()
        : parseFilterDate(dataFinalParam);

    const newFilters: MDFeFiltrarDto = {
      codigoStatusEnvio:
        useFilter.searchParams.get("codigoStatusEnvio") || null,
      dataEmissaoInicial,
      dataEmissaoFinal,
      serie:
        serieParam && Number(serieParam) > 0 ? Number(serieParam) : null,
      placa: placaParam || null,
    };

    useFilter.setFilters((prev) => {
      if (
        prev.codigoStatusEnvio === newFilters.codigoStatusEnvio &&
        prev.serie === newFilters.serie &&
        prev.placa === newFilters.placa &&
        areDatesSameDay(prev.dataEmissaoInicial, newFilters.dataEmissaoInicial) &&
        areDatesSameDay(prev.dataEmissaoFinal, newFilters.dataEmissaoFinal)
      ) {
        return prev;
      }
      return newFilters;
    });
  }, [useFilter.searchParams, useFilter.setFilters]);

  const handleSelectStatus = (eventKey?: string | null) => {
    useFilter.updateFilter(
      "codigoStatusEnvio",
      eventKey === "99" ? null : eventKey
    );
  };

  const handleChangeSerie = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");
    useFilter.updateFilter(
      "serie",
      rawValue !== "" && Number(rawValue) > 0 ? Number(rawValue) : null
    );
  };

  const handleChangePlaca = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.trim().toUpperCase();
    useFilter.updateFilter("placa", rawValue !== "" ? rawValue : null);
  };

  const handleSetDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (!start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: null,
        dataEmissaoFinal: null,
        semData: "true",
      } as any);
    } else if (start && !end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: null,
      }));
    } else if (start && end) {
      useFilter.setFilters((prev) => ({
        ...prev,
        dataEmissaoInicial: start,
        dataEmissaoFinal: end,
      }));
      useFilter.setMultipleFilters({
        dataEmissaoInicial: moment(start).format("YYYY-MM-DD"),
        dataEmissaoFinal: moment(end).format("YYYY-MM-DD"),
        semData: null,
      } as any);
    }
  };

  const formatDate = (date: any) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : "Selecionar";

  const getDatePickerTitle = () => {
    const { dataEmissaoInicial, dataEmissaoFinal } = useFilter.filters;

    if (!dataEmissaoInicial || !dataEmissaoFinal) {
      if (dataEmissaoInicial && !dataEmissaoFinal) {
        return `${formatDate(dataEmissaoInicial)} - Selecionar`;
      }
      return "Emissão";
    }

    return `${formatDate(dataEmissaoInicial)} - ${formatDate(
      dataEmissaoFinal
    )}`;
  };

  const onReturnModalPropriedades = () => {
    setModalShowPropriedades(false);
  };

  const onDeleteModalPropriedades = async (value: MDFeOutput) => {
    if (!value.id) return;
    setLoading(true);
    await MDFeExcluir(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
        }
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao excluir MDF-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onFetchStatusModalPropriedades = async (value: MDFeOutput) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await MDFeConsultarSituacao(value.id)
      .then((response) => {
        filtrar(useFilter.filters);
        MapearErrosServidor(response.data.notifications, showToast);
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao consultar situação",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onSendModalPropriedades = async (value: MDFeOutput) => {
    if (!value.id) return;
    onReturnModalPropriedades();
    setLoading(true);
    await MDFeEnviar(value.id)
      .then((response) => {
        if (response.data.success === true) {
          filtrar(useFilter.filters);
          updateLocal<MDFeInput>(STORAGEMDFE_KEY, null);
          showToast({
            message: "MDF-e autorizado com sucesso!",
            title: "Sucesso",
            variant: "Success",
          });
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
          // Se rejeição for 611 (manifesto não encerrado para placa), sugere abrir pendências
          const has611 = response.data.notifications?.some((n: any) =>
            n.message?.includes("611") || n.message?.toLowerCase().includes("não encerrado")
          );
          if (has611) {
            setModalNaoEncerradosShow(true);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        showToast({
          message: "Ocorreu erro ao enviar MDF-e",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onDownloadXml = async (value: MDFeOutput) => {
    if (!value.id) return;
    setLoading(true);
    setModalShowPropriedades(false);

    try {
      const nomeArquivo = `MDFe_${value.numeroMDFe}`;
      const response = await MDFeBuscarXML([value.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast({
        message: "Ocorreu erro ao baixar XML",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const onPrintModalPropriedadesImprimir = async (value: MDFeOutput) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await MDFeImprimirDAMDFE(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const isMobile = /Android|iPhone/i.test(navigator.userAgent);

          if (isMobile) {
            window.open(pdfUrl, "_blank");
          } else {
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
            };
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
        }
      })
      .catch((error) => {
        console.error("Erro ao imprimir DAMDFE:", error);
        showToast({
          message: "Ocorreu erro ao imprimir DAMDFE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesDownloadDamdfe = async (value: MDFeOutput) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await MDFeImprimirDAMDFE(value.id)
      .then((response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `DAMDFE_${value.numeroMDFe}.pdf`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
        }
      })
      .catch((error) => {
        console.error("Erro ao baixar DAMDFE:", error);
        showToast({
          message: "Ocorreu erro ao baixar DAMDFE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onModalPropriedadesShareDamdfe = async (value: MDFeOutput) => {
    if (!value.id) return;
    onReturnModalPropriedades();

    setLoading(true);
    await MDFeImprimirDAMDFE(value.id)
      .then(async (response) => {
        if (response.data instanceof Blob) {
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          try {
            const file = new File([pdfBlob], `DAMDFE_${value.numeroMDFe}.pdf`, {
              type: "application/pdf",
            });

            if (navigator.canShare?.({ files: [file] })) {
              await navigator.share({
                title: `DAMDFE MDF-e ${value.numeroMDFe}`,
                files: [file],
              });
            } else {
              onModalPropriedadesDownloadDamdfe(value);
            }
          } catch (err) {
            console.error("Erro ao compartilhar:", err);
          }
        } else {
          MapearErrosServidor(response.data.notifications, showToast);
        }
      })
      .catch((error) => {
        console.error("Erro ao compartilhar DAMDFE:", error);
        showToast({
          message: "Ocorreu erro ao compartilhar DAMDFE",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleClickPropriedades = (value: MDFeOutput) => () => {
    setModalShowPropriedades(true);
    setMdfe(value);
  };

  const onEditModalPropriedades = (value: MDFeOutput) => {
    setModalShowPropriedades(false);
    setLoading(true);
    MDFeFiltrar({ id: value.id })
      .then((response) => {
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const mdfeCompleto = response.data.data[0];
          let proprietario = mdfeCompleto.proprietarioVeiculoTracao;
          if (proprietario) {
            if (!proprietario.documento) {
              proprietario.documento =
                proprietario.cnpj || proprietario.cpf || "";
            }
          } else if (
            mdfeCompleto.documentoProprietarioVeiculoTracao ||
            mdfeCompleto.cpfProprietarioVeiculoTracao ||
            mdfeCompleto.cnpjProprietarioVeiculoTracao ||
            mdfeCompleto.nomeProprietarioVeiculoTracao
          ) {
            const doc =
              mdfeCompleto.documentoProprietarioVeiculoTracao ||
              mdfeCompleto.cnpjProprietarioVeiculoTracao ||
              mdfeCompleto.cpfProprietarioVeiculoTracao ||
              "";
            proprietario = {
              documento: doc,
              cpf: mdfeCompleto.cpfProprietarioVeiculoTracao,
              cnpj: mdfeCompleto.cnpjProprietarioVeiculoTracao,
              rntrc: mdfeCompleto.rntrcProprietarioVeiculoTracao || "",
              nome: mdfeCompleto.nomeProprietarioVeiculoTracao || "",
              ie: mdfeCompleto.ieProprietarioVeiculoTracao,
              uf:
                mdfeCompleto.ufProprietarioVeiculoTracao ||
                mdfeCompleto.ufVeiculoTracao,
              tipoProprietario:
                mdfeCompleto.tipoProprietarioVeiculoTracao || 0,
            };
          }

          updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
            id: mdfeCompleto.id,
            serie: mdfeCompleto.serie,
            numeroMDFe: mdfeCompleto.numeroMDFe,
            emissaoDataHora: mdfeCompleto.emissaoDataHora,
            tipoEmitente: mdfeCompleto.tipoEmitente,
            tipoTransportador: mdfeCompleto.tipoTransportador,
            modal: mdfeCompleto.modal,
            ufInicio: mdfeCompleto.ufInicio,
            ufFim: mdfeCompleto.ufFim,
            previsaoInicioViagemDataHora: mdfeCompleto.previsaoInicioViagemDataHora,
            rntrc: mdfeCompleto.rntrc,
            placaVeiculoTracao: mdfeCompleto.placaVeiculoTracao,
            renavamVeiculoTracao: mdfeCompleto.renavamVeiculoTracao,
            taraVeiculoTracao: mdfeCompleto.taraVeiculoTracao,
            capacidadeKGVeiculoTracao: mdfeCompleto.capacidadeKGVeiculoTracao,
            capacidadeM3VeiculoTracao: mdfeCompleto.capacidadeM3VeiculoTracao,
            tipoRodadoVeiculoTracao: mdfeCompleto.tipoRodadoVeiculoTracao,
            tipoCarroceriaVeiculoTracao: mdfeCompleto.tipoCarroceriaVeiculoTracao,
            ufVeiculoTracao: mdfeCompleto.ufVeiculoTracao,
            proprietarioVeiculoTracao: proprietario,
            valorTotalCarga: mdfeCompleto.valorTotalCarga,
            pesoBrutoTotal: mdfeCompleto.pesoBrutoTotal,
            unidadeMedidaPeso: mdfeCompleto.unidadeMedidaPeso,
            informacoesAdicionaisFisco: mdfeCompleto.informacoesAdicionaisFisco,
            informacoesComplementaresContribuinte: mdfeCompleto.informacoesComplementaresContribuinte,
            autorizacao: mdfeCompleto.autorizacao,
            municipiosCarregamento: mdfeCompleto.municipiosCarregamento,
            percursos: mdfeCompleto.percursos,
            municipiosDescarga: mdfeCompleto.municipiosDescarga,
            condutores: mdfeCompleto.condutores,
            reboques: mdfeCompleto.reboques,
            seguros: mdfeCompleto.seguros,
            produtoPredominante: mdfeCompleto.produtoPredominante,
            ciots: mdfeCompleto.ciots || mdfeCompleto.cioTs || [],
            valesPedagio: mdfeCompleto.valesPedagio,
            contratantes: mdfeCompleto.contratantes,
            pagamentos: mdfeCompleto.pagamentos,
          });
          navigate("/fiscal/mdfe/cadastro");
        } else {
          showToast({
            message: "Erro ao carregar dados completos do MDF-e para edição.",
            title: "Erro",
            variant: "Danger",
          });
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar MDF-e completo:", error);
        showToast({
          message: "Erro ao buscar MDF-e para edição.",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onDuplicateModalPropriedades = (value: MDFeOutput) => {
    setModalShowPropriedades(false);
    setLoading(true);
    MDFeFiltrar({ id: value.id })
      .then((response) => {
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const mdfeCompleto = response.data.data[0];
          let proprietario = mdfeCompleto.proprietarioVeiculoTracao;
          if (proprietario) {
            if (!proprietario.documento) {
              proprietario.documento =
                proprietario.cnpj || proprietario.cpf || "";
            }
          } else if (
            mdfeCompleto.documentoProprietarioVeiculoTracao ||
            mdfeCompleto.cpfProprietarioVeiculoTracao ||
            mdfeCompleto.cnpjProprietarioVeiculoTracao ||
            mdfeCompleto.nomeProprietarioVeiculoTracao
          ) {
            const doc =
              mdfeCompleto.documentoProprietarioVeiculoTracao ||
              mdfeCompleto.cnpjProprietarioVeiculoTracao ||
              mdfeCompleto.cpfProprietarioVeiculoTracao ||
              "";
            proprietario = {
              documento: doc,
              cpf: mdfeCompleto.cpfProprietarioVeiculoTracao,
              cnpj: mdfeCompleto.cnpjProprietarioVeiculoTracao,
              rntrc: mdfeCompleto.rntrcProprietarioVeiculoTracao || "",
              nome: mdfeCompleto.nomeProprietarioVeiculoTracao || "",
              ie: mdfeCompleto.ieProprietarioVeiculoTracao,
              uf:
                mdfeCompleto.ufProprietarioVeiculoTracao ||
                mdfeCompleto.ufVeiculoTracao,
              tipoProprietario:
                mdfeCompleto.tipoProprietarioVeiculoTracao || 0,
            };
          }

          updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
            id: 0,
            numeroMDFe: 0,
            serie: mdfeCompleto.serie,
            tipoEmitente: mdfeCompleto.tipoEmitente,
            tipoTransportador: mdfeCompleto.tipoTransportador,
            modal: mdfeCompleto.modal,
            ufInicio: mdfeCompleto.ufInicio,
            ufFim: mdfeCompleto.ufFim,
            previsaoInicioViagemDataHora: mdfeCompleto.previsaoInicioViagemDataHora,
            rntrc: mdfeCompleto.rntrc,
            placaVeiculoTracao: mdfeCompleto.placaVeiculoTracao,
            renavamVeiculoTracao: mdfeCompleto.renavamVeiculoTracao,
            taraVeiculoTracao: mdfeCompleto.taraVeiculoTracao,
            capacidadeKGVeiculoTracao: mdfeCompleto.capacidadeKGVeiculoTracao,
            capacidadeM3VeiculoTracao: mdfeCompleto.capacidadeM3VeiculoTracao,
            tipoRodadoVeiculoTracao: mdfeCompleto.tipoRodadoVeiculoTracao,
            tipoCarroceriaVeiculoTracao: mdfeCompleto.tipoCarroceriaVeiculoTracao,
            ufVeiculoTracao: mdfeCompleto.ufVeiculoTracao,
            proprietarioVeiculoTracao: proprietario,
            valorTotalCarga: mdfeCompleto.valorTotalCarga,
            pesoBrutoTotal: mdfeCompleto.pesoBrutoTotal,
            unidadeMedidaPeso: mdfeCompleto.unidadeMedidaPeso,
            informacoesAdicionaisFisco: mdfeCompleto.informacoesAdicionaisFisco,
            informacoesComplementaresContribuinte: mdfeCompleto.informacoesComplementaresContribuinte,
            municipiosCarregamento: mdfeCompleto.municipiosCarregamento,
            percursos: mdfeCompleto.percursos,
            municipiosDescarga: mdfeCompleto.municipiosDescarga,
            condutores: mdfeCompleto.condutores,
            reboques: mdfeCompleto.reboques,
            seguros: mdfeCompleto.seguros,
            produtoPredominante: mdfeCompleto.produtoPredominante,
            ciots: mdfeCompleto.ciots || mdfeCompleto.cioTs || [],
            valesPedagio: mdfeCompleto.valesPedagio,
            contratantes: mdfeCompleto.contratantes,
            pagamentos: mdfeCompleto.pagamentos,
          });
          navigate("/fiscal/mdfe/cadastro");
        } else {
          showToast({
            message: "Erro ao carregar dados do MDF-e para duplicação.",
            title: "Erro",
            variant: "Danger",
          });
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar MDF-e para duplicação:", error);
        showToast({
          message: "Erro ao duplicar MDF-e.",
          title: "Erro",
          variant: "Danger",
        });
      })
      .finally(() => setLoading(false));
  };

  const onEventModalPropriedades = (value: MDFeOutput) => {
    setModalShowPropriedades(false);
    navigate(`/fiscal/mdfe/evento?mdfeId=${value.id}`);
  };

  const onEncerrarModalPropriedades = (value: MDFeOutput) => {
    setModalShowPropriedades(false);
    const ultDescarga =
      value.municipiosDescarga && value.municipiosDescarga.length > 0
        ? value.municipiosDescarga[value.municipiosDescarga.length - 1]
        : null;
    const codMun =
      ultDescarga?.codigoMunicipioDescarga ||
      (ultDescarga as any)?.codigoMunicipio ||
      "";
    const nomeMun =
      ultDescarga?.nomeMunicipioDescarga ||
      (ultDescarga as any)?.nomeMunicipio ||
      "";
    const queryMun = codMun
      ? `&codigoMunicipio=${codMun}&nomeMunicipio=${encodeURIComponent(nomeMun)}`
      : "";
    navigate(
      `/fiscal/mdfe/evento?mdfeId=${value.id}&action=encerrar&tipoEvento=110112${queryMun}`
    );
  };

  const onVisualizarModalPropriedades = (value: MDFeOutput) => {
    if (!value.id) return;
    setModalShowPropriedades(false);
    navigate(`/fiscal/mdfe/${value.id}/visualizar`, { state: { mdfe: value } });
  };

  const onContinuarModalNovo = () => {
    setModalNovoShow(false);
    navigate("/fiscal/mdfe/cadastro");
  };

  const onCriarNovoModalNovo = () => {
    setModalNovoShow(false);
    updateLocal<MDFeInput>(STORAGEMDFE_KEY, null);
    navigate("/fiscal/mdfe/cadastro");
  };

  const onCancelModalNovo = () => {
    setModalNovoShow(false);
  };

  const onEncerrarFromNaoEncerrados = async (chave: string) => {
    setModalNaoEncerradosShow(false);
    let found = data.find((d) => d.autorizacao?.chaveAcesso === chave);
    let mdfeId = found?.id;
    let ultDescarga =
      found?.municipiosDescarga && found.municipiosDescarga.length > 0
        ? found.municipiosDescarga[found.municipiosDescarga.length - 1]
        : null;

    if (!mdfeId && chave && chave.length === 44) {
      const serie = Number(chave.substring(22, 25));
      const numeroMDFe = Number(chave.substring(25, 34));
      if (numeroMDFe > 0) {
        try {
          const resp = await MDFeFiltrar({ serie, numeroMDFe } as any);
          if (resp.data.success && resp.data.data?.length > 0) {
            const item = resp.data.data[0];
            mdfeId = item.id;
            ultDescarga =
              item.municipiosDescarga && item.municipiosDescarga.length > 0
                ? item.municipiosDescarga[item.municipiosDescarga.length - 1]
                : null;
          }
        } catch (e) {
          console.error("Erro ao buscar MDF-e por chave:", e);
        }
      }
    }

    const codMun =
      ultDescarga?.codigoMunicipioDescarga ||
      (ultDescarga as any)?.codigoMunicipio ||
      "";
    const nomeMun =
      ultDescarga?.nomeMunicipioDescarga ||
      (ultDescarga as any)?.nomeMunicipio ||
      "";
    const queryMun = codMun
      ? `&codigoMunicipio=${codMun}&nomeMunicipio=${encodeURIComponent(nomeMun)}`
      : "";

    if (mdfeId) {
      navigate(
        `/fiscal/mdfe/evento?mdfeId=${mdfeId}&action=encerrar&tipoEvento=110112${queryMun}`
      );
    } else {
      navigate(
        `/fiscal/mdfe/evento?chave=${chave}&action=encerrar&tipoEvento=110112`
      );
    }
  };

  return {
    data,
    loading,
    handleChangeSerie,
    handleChangePlaca,
    handleSelectStatus,
    handleSetDates,
    useFilter,
    getDatePickerTitle,
    modalShowPropriedades,
    mdfe,
    onReturnModalPropriedades,
    onDeleteModalPropriedades,
    onFetchStatusModalPropriedades,
    onDownloadXml,
    onPrintModalPropriedadesImprimir,
    onModalPropriedadesDownloadDamdfe,
    onModalPropriedadesShareDamdfe,
    handleClickPropriedades,
    onSendModalPropriedades,
    onEditModalPropriedades,
    onDuplicateModalPropriedades,
    onEventModalPropriedades,
    onEncerrarModalPropriedades,
    onVisualizarModalPropriedades,
    modalNovoShow,
    onContinuarModalNovo,
    onCriarNovoModalNovo,
    onCancelModalNovo,
    modalNaoEncerradosShow,
    setModalNaoEncerradosShow,
    onEncerrarFromNaoEncerrados,
    menu,
  };
};

export default MDFeCode;
